import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Receipt, 
  Search, 
  Plus,
  Phone,
  User,
  CreditCard,
  Clock,
  CheckCircle2,
  X,
  RefreshCw,
  IndianRupee,
  UserCheck,
  UserPlus,
  ArrowRight,
  ArrowLeft,
  Briefcase,
  Check,
  ChevronsUpDown,
  Package as PackageIcon,
  Tag
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import PaymentReceipt from '../components/PaymentReceipt';
import { RewardUpsellBanner } from '../components/RewardUpsellBanner';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { format } from 'date-fns';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { cn } from '../lib/utils';
import PackagePOS from '../components/PackagePOS';

const TellerPage = ({ user, onLogout }) => {
  const [appointments, setAppointments] = useState([]);
  const [payments, setPayments] = useState([]);
  const [services, setServices] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [counsellors, setCounsellors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('appointments');
  
  // Date state - Admin can navigate, Teller is locked to today
  const [selectedDate, setSelectedDate] = useState(new Date());
  const isAdmin = user?.role === 'Admin';
  
  // Format selected date for API calls
  const dateStr = format(selectedDate, 'yyyy-MM-dd');
  const isToday = format(new Date(), 'yyyy-MM-dd') === dateStr;

  // Step-by-step Payment Modal State
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [phoneInput, setPhoneInput] = useState('');
  const [patientsFound, setPatientsFound] = useState([]); // Multiple patients
  const [selectedPatient, setSelectedPatient] = useState(null); // Selected from list
  const [lookupLoading, setLookupLoading] = useState(false);
  const [isNewPatient, setIsNewPatient] = useState(false);
  const [newPatientName, setNewPatientName] = useState('');
  const [selectedService, setSelectedService] = useState(null);
  const [amount, setAmount] = useState('');
  const [paymentSplits, setPaymentSplits] = useState([]); // Array of {method, amount}
  const [selectedCounsellor, setSelectedCounsellor] = useState(null);
  const [remarks, setRemarks] = useState('');

  // Cancel Modal State
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [receiptPayment, setReceiptPayment] = useState(null);
  const [cancellingPayment, setCancellingPayment] = useState(null);
  const [cancelReason, setCancelReason] = useState('');

  // Google-review coupon state (payment method component)
  const COUPON_METHOD_ID = '__coupon__';
  const [activeCoupons, setActiveCoupons] = useState([]);
  const [selectedCoupon, setSelectedCoupon] = useState(null); // Full coupon doc when applied
  const [couponsLoading, setCouponsLoading] = useState(false);

  // Date navigation functions (Admin only)
  const goToPrevDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    setSelectedDate(newDate);
  };

  const goToNextDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    setSelectedDate(newDate);
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const searchParam = searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : '';
      const [appointmentsRes, paymentsRes, servicesRes, methodsRes, counsellorsRes] = await Promise.all([
        api.get(`/teller/appointments?date=${dateStr}${searchParam}`),
        api.get(`/payments?date=${dateStr}`),
        api.get('/services'),
        api.get('/payment-methods'),
        api.get('/counsellors')
      ]);
      
      setAppointments(appointmentsRes.data);
      setPayments(paymentsRes.data);
      setServices(servicesRes.data);
      setPaymentMethods(methodsRes.data.filter(m => m.is_active));
      setCounsellors(counsellorsRes.data.filter(c => c.is_active));
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [dateStr, searchQuery]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Silent refresh so receipts drop off the Teller list after their 10-minute window
  useEffect(() => {
    if (isAdmin) return;
    const t = setInterval(() => {
      api.get(`/payments?date=${dateStr}`).then(res => setPayments(res.data)).catch(() => {});
    }, 60000);
    return () => clearInterval(t);
  }, [dateStr, isAdmin]);

  // Phone lookup - returns all patients with this phone
  const lookupPatient = async (phone) => {
    if (phone.length < 10) {
      setPatientsFound([]);
      setSelectedPatient(null);
      setIsNewPatient(false);
      return;
    }
    
    try {
      setLookupLoading(true);
      const res = await api.get(`/patients/by-phone/${phone}`);
      if (res.data && res.data.length > 0) {
        setPatientsFound(res.data);
        setSelectedPatient(null); // User needs to select
        setIsNewPatient(false);
      } else {
        setPatientsFound([]);
        setSelectedPatient(null);
        setIsNewPatient(true);
      }
    } catch (error) {
      setPatientsFound([]);
      setSelectedPatient(null);
      setIsNewPatient(true);
    } finally {
      setLookupLoading(false);
    }
  };

  const openPaymentModal = (appointment = null) => {
    // Reset coupon state on every open
    setActiveCoupons([]);
    setSelectedCoupon(null);
    if (appointment) {
      // From appointment - pre-fill data
      setSelectedAppointment(appointment);
      setPhoneInput(appointment.patient_phone || '');
      setPatientsFound([]);
      setSelectedPatient({ name: appointment.patient_name, id: appointment.patient_id });
      setIsNewPatient(false);
      setNewPatientName('');
      setSelectedService(services.find(s => s.id === appointment.service_id) || null);
      setAmount('');
      setPaymentSplits([]);
      setSelectedCounsellor(null);
      setRemarks('');
      setCurrentStep(3); // Skip to service/amount step since patient is known
      // Prefetch coupons for this patient
      if (appointment.patient_id) fetchActiveCoupons(appointment.patient_id);
    } else {
      // New entry - start from scratch
      setSelectedAppointment(null);
      setPhoneInput('');
      setPatientsFound([]);
      setSelectedPatient(null);
      setIsNewPatient(false);
      setNewPatientName('');
      setSelectedService(null);
      setAmount('');
      setPaymentSplits([]);
      setSelectedCounsellor(null);
      setRemarks('');
      setCurrentStep(1);
    }
    setPaymentModalOpen(true);
  };

  // Start new payment with pre-filled phone number
  const startPaymentWithPhone = (phoneNumber) => {
    setSelectedAppointment(null);
    setPhoneInput(phoneNumber || '');
    setPatientsFound([]);
    setSelectedPatient(null);
    setIsNewPatient(false);
    setNewPatientName('');
    setSelectedService(null);
    setAmount('');
    setPaymentSplits([]);
    setSelectedCounsellor(null);
    setRemarks('');
    setCurrentStep(1);
    setPaymentModalOpen(true);
    
    // Auto-lookup if phone is valid
    if (phoneNumber && phoneNumber.length >= 10) {
      lookupPatient(phoneNumber);
    }
  };

  const handlePhoneSubmit = () => {
    if (phoneInput.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }
    setCurrentStep(2);
  };

  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
    setIsNewPatient(false);
    setCurrentStep(3);
    if (patient?.id) fetchActiveCoupons(patient.id);
  };

  const fetchActiveCoupons = async (patientId) => {
    if (!patientId) return;
    try {
      setCouponsLoading(true);
      const res = await api.get(`/coupons/patient/${patientId}/active`);
      setActiveCoupons(res.data?.rows || []);
    } catch (_e) {
      setActiveCoupons([]);
    } finally {
      setCouponsLoading(false);
    }
  };

  const applyCouponToPayment = (coupon) => {
    const bill = parseFloat(amount);
    const cVal = Number(coupon.discount_amount || 0);
    if (!bill || bill < cVal) {
      toast.error(`Bill amount (₹${bill || 0}) must be at least the coupon value (₹${cVal})`);
      return;
    }
    if (paymentSplits.some(s => s.method?.id === COUPON_METHOD_ID)) {
      toast.error('A coupon is already applied to this payment');
      return;
    }
    const remaining = bill - paymentSplits.reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0);
    if (remaining < cVal) {
      toast.error(`Not enough remaining balance (₹${remaining}) to apply this ₹${cVal} coupon`);
      return;
    }
    setSelectedCoupon(coupon);
    setPaymentSplits([
      ...paymentSplits,
      {
        method: {
          id: COUPON_METHOD_ID,
          name: `Coupon (${coupon.code})`,
          coupon_id: coupon.id,
        },
        amount: String(cVal),
        locked: true,
      },
    ]);
  };

  const handleAddNewPatient = () => {
    setSelectedPatient(null);
    setIsNewPatient(true);
  };

  const handleNewPatientSubmit = () => {
    if (!newPatientName.trim()) {
      toast.error('Please enter patient name');
      return;
    }
    setCurrentStep(3);
  };

  const handleServiceSelect = async (service) => {
    // Redemption from a package is ONLY allowed when the payment is being
    // collected against an existing appointment for the SAME service. If the
    // teller is doing a freeform payment (no appointment) OR the appointment
    // is for a different service, we skip the redeem prompt entirely.
    const canRedeem = !!(selectedAppointment
      && selectedAppointment.service_id === service?.id
      && ['scheduled', 'confirmed', 'checked_in', 'pending', 'booked'].includes(String(selectedAppointment.status || '').toLowerCase()));

    if (canRedeem && service?.id) {
      try {
        const res = await api.get('/packages', {
          params: { patient_id: selectedAppointment.patient_id, status: 'active', limit: 50 },
        });
        const matches = (res.data?.rows || []).filter(pkg => {
          const it = (pkg.items || []).find(i => i.service_id === service.id);
          return it && (Number(it.quantity_total || 0) - Number(it.quantity_consumed || 0)) > 0;
        });
        if (matches.length > 0) {
          setRedeemPrompt({ service, packages: matches, appointment: selectedAppointment });
          return; // wait for teller's decision
        }
      } catch (e) {
        // Non-fatal — fall through to regular flow
      }
    }
    setSelectedService(service);
    setCurrentStep(4);
  };

  // ==== Redeem-from-package flow ====
  const [redeemPrompt, setRedeemPrompt] = useState(null); // {service, packages[]}
  const [redeemChoice, setRedeemChoice] = useState(null); // package id
  const [redeemSubmitting, setRedeemSubmitting] = useState(false);

  const confirmRedeem = async () => {
    if (!redeemPrompt || !redeemChoice) return;
    if (!redeemPrompt.appointment?.id) {
      toast.error('An appointment is required to redeem — please start from a pending appointment');
      return;
    }
    try {
      setRedeemSubmitting(true);
      const redeemRes = await api.post(`/packages/${redeemChoice}/consume`, {
        service_id: redeemPrompt.service.id,
        appointment_id: redeemPrompt.appointment.id,
      });
      toast.success(`Redeemed 1× ${redeemPrompt.service.name} from package`);
      if (redeemRes.data?.receipt) setReceiptPayment(redeemRes.data.receipt);
      // Reset the POS flow — package consumption doesn't create a POS receipt
      setRedeemPrompt(null);
      setRedeemChoice(null);
      setPaymentModalOpen(false);
      setCurrentStep(1);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to redeem');
    } finally {
      setRedeemSubmitting(false);
    }
  };

  const skipRedeem = () => {
    // Teller declined — proceed with normal payment flow for the same service.
    const service = redeemPrompt?.service;
    setRedeemPrompt(null);
    setRedeemChoice(null);
    if (service) {
      setSelectedService(service);
      setCurrentStep(4);
    }
  };

  const handleAmountSubmit = () => {
    if (amount === '' || parseFloat(amount) < 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    setCurrentStep(5);
  };

  const addPaymentSplit = (method) => {
    // Check if method already added
    if (paymentSplits.find(s => s.method.id === method.id)) {
      toast.error('This payment method is already added');
      return;
    }
    
    // Calculate remaining amount
    const allocated = paymentSplits.reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0);
    const remaining = parseFloat(amount) - allocated;
    
    setPaymentSplits([...paymentSplits, { method, amount: remaining > 0 ? remaining.toString() : '' }]);
  };

  const updateSplitAmount = (index, newAmount) => {
    const updated = [...paymentSplits];
    if (updated[index]?.locked || updated[index]?.method?.id === COUPON_METHOD_ID) {
      // Coupon amount is fixed to the coupon value — silently ignore edits
      return;
    }
    updated[index].amount = newAmount;
    setPaymentSplits(updated);
  };

  const removeSplit = (index) => {
    const target = paymentSplits[index];
    if (target?.method?.id === COUPON_METHOD_ID) {
      setSelectedCoupon(null);
    }
    setPaymentSplits(paymentSplits.filter((_, i) => i !== index));
  };

  const getTotalAllocated = () => {
    return paymentSplits.reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0);
  };

  const getRemainingAmount = () => {
    return parseFloat(amount) - getTotalAllocated();
  };

  const handlePaymentMethodDone = () => {
    const totalAllocated = getTotalAllocated();
    const totalAmount = parseFloat(amount);
    
    if (paymentSplits.length === 0) {
      toast.error('Please add at least one payment method');
      return;
    }
    
    if (Math.abs(totalAllocated - totalAmount) > 0.01) {
      toast.error(`Total allocated (₹${totalAllocated}) must equal total amount (₹${totalAmount})`);
      return;
    }
    
    setCurrentStep(6);
  };

  const handleCounsellorSelect = (counsellor) => {
    setSelectedCounsellor(counsellor);
    setCurrentStep(7);
  };

  const handleSavePayment = async () => {
    try {
      // Validate required fields first
      if (!paymentSplits || paymentSplits.length === 0) {
        toast.error('Please select a payment method');
        return;
      }
      
      if (!selectedCounsellor) {
        toast.error('Please select a counsellor');
        return;
      }
      
      // Use appointment's service_id if available, otherwise use selectedService
      const serviceId = selectedAppointment?.service_id || selectedService?.id;
      
      if (!serviceId) {
        toast.error('Please select a service');
        return;
      }
      
      if (amount === '' || amount === null || amount === undefined || parseFloat(amount) < 0) {
        toast.error('Please enter a valid amount');
        return;
      }
      
      const payloadSplits = paymentSplits.map(s => ({
        payment_method_id: s.method?.id,
        amount: parseFloat(s.amount) || 0
      })).filter(s => s.payment_method_id);
      
      if (payloadSplits.length === 0) {
        toast.error('Invalid payment method selection');
        return;
      }
      
      const payRes = await api.post('/payments', {
        appointment_id: selectedAppointment?.id || null,
        patient_phone: phoneInput,
        patient_name: isNewPatient ? newPatientName : undefined,
        patient_id: selectedPatient?.id || undefined,
        service_id: serviceId,
        amount: parseFloat(amount),
        payment_splits: payloadSplits,
        counsellor_id: selectedCounsellor.id,
        remarks: remarks || '',
        coupon_id: selectedCoupon?.id || undefined,
      });
      toast.success('Payment created successfully!');
      setPaymentModalOpen(false);
      setReceiptPayment(payRes.data);
      fetchData();
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create payment');
    }
  };

  const handleCancelPayment = async () => {
    if (!cancellingPayment) return;
    
    try {
      await api.post(`/payments/${cancellingPayment.id}/cancel`, {
        reason: cancelReason
      });
      toast.success('Payment cancelled');
      setCancelModalOpen(false);
      setCancellingPayment(null);
      setCancelReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel payment');
    }
  };

  // Only show unpaid appointments in the Pending tab
  const filteredAppointments = appointments.filter(a => !a.is_paid);
  
  // Admin + Teller see today's receipts; only Admin can cancel (button gated below)
  const filteredPayments = payments.filter(p => {
    const matchesSearch = !searchQuery || 
      p.patient_phone?.includes(searchQuery) || 
      p.patient_name?.toLowerCase().includes(searchQuery.toLowerCase());
    
    if (isAdmin) {
      // Admin sees all payments (completed + cancelled)
      return matchesSearch;
    } else {
      // Teller sees today's receipts too (read-only) so they can reprint / resend on WhatsApp
      return matchesSearch;
    }
  });

  const unpaidCount = appointments.filter(a => !a.is_paid).length;
  const paidCount = payments.filter(p => p.status === 'completed').length;
  const cancelledCount = payments.filter(p => p.status === 'cancelled').length;
  
  // Calculate totals by payment method for Admin view only
  const totalsByMethod = {};
  let grandTotal = 0;
  if (user?.role === 'Admin') {
    payments.filter(p => p.status === 'completed').forEach(p => {
      if (p.payment_splits && p.payment_splits.length > 0) {
        p.payment_splits.forEach(split => {
          const methodName = split.payment_method_name || 'Unknown';
          if (!totalsByMethod[methodName]) {
            totalsByMethod[methodName] = 0;
          }
          totalsByMethod[methodName] += split.amount || 0;
        });
      } else {
        const methodName = p.payment_method_name || 'Unknown';
        if (!totalsByMethod[methodName]) {
          totalsByMethod[methodName] = 0;
        }
        totalsByMethod[methodName] += p.amount;
      }
      grandTotal += p.amount;
    });
  }

  // Step indicator component
  const StepIndicator = ({ step, label, isActive, isCompleted }) => (
    <div className={`flex items-center gap-2 ${isActive ? 'text-green-600' : isCompleted ? 'text-gray-500' : 'text-gray-300'}`}>
      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
        isActive ? 'bg-green-600 text-white' : isCompleted ? 'bg-green-100 text-green-600' : 'bg-gray-100'
      }`}>
        {isCompleted ? '✓' : step}
      </div>
      <span className="text-xs hidden sm:inline">{label}</span>
    </div>
  );

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="px-4 py-3 max-w-lg mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Receipt className="w-6 h-6 text-green-600" />
              Teller / POS
            </h1>
            {/* Date display with navigation for Admin */}
            {isAdmin ? (
              <div className="flex items-center gap-2 mt-1">
                <button 
                  onClick={goToPrevDay}
                  className="p-1 hover:bg-gray-100 rounded transition"
                >
                  <ArrowLeft className="w-4 h-4 text-gray-500" />
                </button>
                <button 
                  onClick={goToToday}
                  className={`text-sm px-2 py-0.5 rounded ${isToday ? 'bg-green-100 text-green-700 font-medium' : 'text-gray-600 hover:bg-gray-100'}`}
                >
                  {format(selectedDate, 'EEE, MMM d, yyyy')}
                </button>
                <button 
                  onClick={goToNextDay}
                  className="p-1 hover:bg-gray-100 rounded transition"
                >
                  <ArrowRight className="w-4 h-4 text-gray-500" />
                </button>
                {!isToday && (
                  <button 
                    onClick={goToToday}
                    className="text-xs text-blue-600 hover:underline ml-1"
                  >
                    Today
                  </button>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500">{format(new Date(), 'EEEE, MMMM d, yyyy')}</p>
            )}
          </div>
          <Button onClick={() => openPaymentModal()} size="sm" className="bg-green-600 hover:bg-green-700" data-testid="teller-new-payment-button">
            <Plus className="w-4 h-4 mr-1" />
            New
          </Button>
        </div>

        {/* Totals Summary Card - Admin Only */}
        {user?.role === 'Admin' && Object.keys(totalsByMethod).length > 0 && (
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl p-4 mb-4 text-white">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium opacity-90">Today's Collection</span>
              <span className="text-2xl font-bold">₹{grandTotal.toLocaleString()}</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(totalsByMethod).map(([method, amt]) => (
                <div key={method} className="bg-white/20 rounded-lg px-3 py-2">
                  <div className="text-xs opacity-90">{method}</div>
                  <div className="font-semibold">₹{amt.toLocaleString()}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search by phone or name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setActiveTab('appointments')}
            className={`flex-1 py-2 px-4 rounded-xl text-sm font-medium transition ${
              activeTab === 'appointments' 
                ? 'bg-orange-500 text-white' 
                : 'bg-gray-100 text-gray-600'
            }`}
          >
            Pending ({unpaidCount})
          </button>
          <button
            onClick={() => setActiveTab('payments')}
            className={`flex-1 py-2 px-4 rounded-xl text-sm font-medium transition ${
              activeTab === 'payments' 
                ? 'bg-green-500 text-white'
                : 'bg-gray-100 text-gray-600'
            }`}
          >
            {`Receipts (${paidCount}${cancelledCount > 0 ? ` • ${cancelledCount} cancelled` : ''})`}
          </button>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex justify-center py-12">
            <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
          </div>
        ) : activeTab === 'appointments' ? (
          /* Appointments List */
          <div className="space-y-3">
            {filteredAppointments.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
                <CheckCircle2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No pending appointments</p>
              </div>
            ) : (
              filteredAppointments.map(apt => (
                <div 
                  key={apt.id}
                  className={`bg-white rounded-xl border p-4 ${
                    apt.is_paid ? 'border-green-200 bg-green-50/50' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {apt.ref_number && (
                        <span className="text-xs font-mono bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded mb-1 inline-block">
                          {apt.ref_number}
                        </span>
                      )}
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900">{apt.patient_name}</h3>
                        {apt.is_paid && (
                          <Badge className="bg-green-100 text-green-700 text-xs">Paid</Badge>
                        )}
                      </div>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          startPaymentWithPhone(apt.patient_phone);
                        }}
                        className="text-sm text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1 cursor-pointer"
                        title="Start new payment with this phone"
                      >
                        <Phone className="w-3 h-3" />
                        {apt.patient_phone || 'No phone'}
                      </button>
                      <p className="text-sm text-gray-600 mt-1">{apt.service_name}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        <Clock className="w-3 h-3 inline mr-1" />
                        {apt.scheduled_time || 'No time'}
                      </p>
                    </div>
                    {!apt.is_paid && (
                      <Button
                        onClick={() => openPaymentModal(apt)}
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <IndianRupee className="w-4 h-4 mr-1" />
                        Pay
                      </Button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        ) : (
          /* Payments/Receipts List */
          <div className="space-y-3">
            {filteredPayments.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
                <Receipt className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No receipts for this date</p>
              </div>
            ) : (
              filteredPayments.map(payment => (
                <div 
                  key={payment.id}
                  className={`bg-white rounded-xl border p-4 ${
                    payment.status === 'cancelled' ? 'border-red-200 bg-red-50/50' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs text-gray-500">{payment.receipt_number}</span>
                        {payment.appointment_ref_number && (
                          <Badge className="bg-indigo-100 text-indigo-700 text-xs">{payment.appointment_ref_number}</Badge>
                        )}
                        {payment.status === 'cancelled' && (
                          <Badge className="bg-red-600 text-white text-xs font-bold px-2">CANCELLED</Badge>
                        )}
                        {payment.payment_splits?.length > 1 && (
                          <Badge className="bg-blue-100 text-blue-700 text-xs">Split</Badge>
                        )}
                      </div>
                      <h3 className={`font-semibold ${payment.status === 'cancelled' ? 'text-gray-500' : 'text-gray-900'}`}>{payment.patient_name}</h3>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          startPaymentWithPhone(payment.patient_phone);
                        }}
                        className="text-sm text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1 cursor-pointer"
                        title="Start new payment with this phone"
                      >
                        <Phone className="w-3 h-3" />
                        {payment.patient_phone}
                      </button>
                    </div>
                    <div className="text-right">
                      <p className={`text-lg font-bold ${payment.status === 'cancelled' ? 'text-gray-400 line-through' : 'text-green-600'}`}>
                        ₹{payment.amount.toLocaleString()}
                      </p>
                      {payment.payment_splits?.length > 1 ? (
                        <div className="text-xs text-gray-500">
                          {payment.payment_splits.map((s, i) => (
                            <div key={i}>{s.payment_method_name}: ₹{s.amount}</div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-500">{payment.payment_method_name}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="text-sm text-gray-600 mb-2">
                    <div className="flex items-center justify-between gap-2">
                      <p>{payment.service_name}</p>
                      <button
                        onClick={() => setReceiptPayment(payment)}
                        className="text-xs text-emerald-700 hover:text-emerald-900 hover:underline flex items-center gap-1"
                        data-testid={`view-receipt-${payment.receipt_number}`}
                      >
                        <Receipt className="w-3 h-3" /> Receipt
                      </button>
                    </div>
                    <p className="text-xs text-gray-400">Collected by: {payment.counsellor_name}</p>
                    {payment.remarks && (
                      <p className="text-xs text-gray-400 mt-1">Note: {payment.remarks}</p>
                    )}
                    {payment.status === 'cancelled' && payment.cancel_reason && (
                      <p className="text-xs text-red-500 mt-1">Cancel reason: {payment.cancel_reason}</p>
                    )}
                  </div>

                  {/* Cancel button - Admin only for completed payments */}
                  {isAdmin && payment.status === 'completed' && (
                    <div className="flex justify-end mt-3 pt-3 border-t border-gray-100">
                      <Button
                        onClick={() => {
                          setCancellingPayment(payment);
                          setCancelModalOpen(true);
                        }}
                        variant="outline"
                        size="sm"
                        className="text-red-600 border-red-200 hover:bg-red-50"
                      >
                        <X className="w-3 h-3 mr-1" />
                        Cancel
                      </Button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      <PaymentReceipt payment={receiptPayment} open={!!receiptPayment} onClose={() => setReceiptPayment(null)} />

      {/* Step-by-Step Payment Modal */}
      <Dialog open={paymentModalOpen} onOpenChange={setPaymentModalOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-green-600" />
              New Payment
            </DialogTitle>
          </DialogHeader>

          {/* Step Progress */}
          <div className="flex justify-between items-center py-2 border-b mb-4">
            <StepIndicator step={1} label="Phone" isActive={currentStep === 1} isCompleted={currentStep > 1} />
            <StepIndicator step={2} label="Patient" isActive={currentStep === 2} isCompleted={currentStep > 2} />
            <StepIndicator step={3} label="Service" isActive={currentStep === 3} isCompleted={currentStep > 3} />
            <StepIndicator step={4} label="Amount" isActive={currentStep === 4} isCompleted={currentStep > 4} />
            <StepIndicator step={5} label="Method" isActive={currentStep === 5} isCompleted={currentStep > 5} />
            <StepIndicator step={6} label="Staff" isActive={currentStep === 6} isCompleted={currentStep > 6} />
            <StepIndicator step={7} label="Confirm" isActive={currentStep === 7} isCompleted={false} />
          </div>

          {/* Step 1: Phone Number */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <div>
                <Label className="text-base font-semibold">Enter Phone Number</Label>
                <p className="text-sm text-gray-500 mb-3">We'll look up all patients with this number</p>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    value={phoneInput}
                    data-testid="teller-phone-input"
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setPhoneInput(val);
                      lookupPatient(val);
                    }}
                    placeholder="10 digit phone number"
                    className="pl-11 text-lg h-12"
                    autoFocus
                  />
                </div>
                
                {lookupLoading && (
                  <p className="text-sm text-gray-500 mt-2 flex items-center gap-2">
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Looking up patients...
                  </p>
                )}
                
                {patientsFound.length > 0 && (
                  <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-sm text-green-700 flex items-center gap-2">
                      <UserCheck className="w-4 h-4" />
                      {patientsFound.length === 1 
                        ? `Found: ${patientsFound[0].name}` 
                        : `Found ${patientsFound.length} patients with this number`}
                    </p>
                  </div>
                )}
                
                {isNewPatient && phoneInput.length >= 10 && (
                  <div className="mt-3 p-3 bg-orange-50 rounded-lg border border-orange-200">
                    <p className="text-sm text-orange-700 flex items-center gap-2">
                      <UserPlus className="w-4 h-4" />
                      No patient found - you can add new patient
                    </p>
                  </div>
                )}
              </div>
              
              <Button 
                onClick={handlePhoneSubmit} 
                className="w-full bg-green-600 hover:bg-green-700"
                data-testid="teller-phone-continue-button"
                disabled={phoneInput.length < 10}
              >
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {/* Step 2: Select Patient from list or Add New */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <div>
                <Label className="text-base font-semibold">
                  {patientsFound.length > 0 ? 'Select Patient' : 'Add New Patient'}
                </Label>
                <p className="text-sm text-gray-500 mb-3">
                  {patientsFound.length > 0 
                    ? `${patientsFound.length} patient(s) found with ${phoneInput}` 
                    : `Create new patient for ${phoneInput}`}
                </p>
                
                {/* List of existing patients */}
                {patientsFound.length > 0 && !isNewPatient && (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {patientsFound.map(patient => (
                      <button
                        key={patient.id}
                        onClick={() => handlePatientSelect(patient)}
                        data-testid={`teller-select-patient-${patient.id}`}
                        className="w-full p-3 rounded-xl border border-gray-200 hover:border-green-500 hover:bg-green-50 transition text-left flex items-center gap-3"
                      >
                        <div className="bg-green-100 text-green-600 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                          <User className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-900 truncate">{patient.name}</p>
                          <p className="text-xs text-gray-500">{patient.primary_phone}</p>
                        </div>
                        <ArrowRight className="w-4 h-4 text-gray-400" />
                      </button>
                    ))}
                    
                    {/* Option to add new patient even if others exist */}
                    <button
                      onClick={handleAddNewPatient}
                      className="w-full p-3 rounded-xl border border-dashed border-orange-300 hover:border-orange-500 hover:bg-orange-50 transition text-left flex items-center gap-3"
                    >
                      <div className="bg-orange-100 text-orange-600 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                        <UserPlus className="w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-orange-700">Add New Patient</p>
                        <p className="text-xs text-orange-500">Create a new patient with this number</p>
                      </div>
                    </button>
                  </div>
                )}
                
                {/* New patient form */}
                {(isNewPatient || patientsFound.length === 0) && (
                  <div className="mt-2">
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input
                        value={newPatientName}
                        onChange={(e) => setNewPatientName(e.target.value)}
                        placeholder="Enter patient name"
                        className="pl-11 text-lg h-12"
                        autoFocus
                      />
                    </div>
                  </div>
                )}
              </div>
              
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    if (isNewPatient && patientsFound.length > 0) {
                      setIsNewPatient(false);
                      setNewPatientName('');
                    } else {
                      setCurrentStep(1);
                    }
                  }} 
                  className="flex-1"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
                {(isNewPatient || patientsFound.length === 0) && (
                  <Button 
                    onClick={handleNewPatientSubmit} 
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    disabled={!newPatientName.trim()}
                  >
                    Continue <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Select Service */}
          {currentStep === 3 && (
            <div className="space-y-4">
              {/* Rewards banner — points, redeemable rewards, AI upsell */}
              <RewardUpsellBanner
                patientId={selectedPatient?.id || selectedAppointment?.patient_id}
                allowRedeem
                onRedeemed={() => fetchData()}
              />
              {/* Packages panel — shown when we know which patient */}
              <PackagePOS
                patientId={selectedPatient?.id || selectedAppointment?.patient_id}
                patientName={selectedPatient?.name || selectedAppointment?.patient_name || newPatientName}
                services={services}
                paymentMethods={paymentMethods}
                counsellors={counsellors}
                userRole={user?.role}
                pendingAppointments={appointments}
                selectedAppointment={selectedAppointment}
                onChanged={fetchData}
                onRedeemed={(appt) => {
                  // If the redeemed appointment is what the teller was collecting for,
                  // close the POS modal — no payment needs to be taken.
                  if (appt && selectedAppointment && appt.id === selectedAppointment.id) {
                    setPaymentModalOpen(false);
                    setCurrentStep(1);
                  }
                }}
              />
              <div>
                <Label className="text-base font-semibold">Select Service</Label>
                <p className="text-sm text-gray-500 mb-3">
                  {selectedAppointment ? `Pre-selected: ${selectedAppointment.service_name}` : 'Choose the service provided'}
                </p>
                
                {selectedAppointment ? (
                  <div className="p-4 bg-green-50 rounded-xl border border-green-200 mb-3">
                    <div className="flex items-center gap-3">
                      <Briefcase className="w-5 h-5 text-green-600" />
                      <span className="font-medium">{selectedAppointment.service_name}</span>
                    </div>
                  </div>
                ) : (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        className="w-full justify-between h-12 text-left font-normal"
                      >
                        {selectedService ? (
                          <div>
                            <div className="font-medium">{selectedService.name}</div>
                            <div className="text-xs text-gray-500">{selectedService.type}</div>
                          </div>
                        ) : (
                          <span className="text-gray-500">Search and select service...</span>
                        )}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-full p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search services..." />
                        <CommandList>
                          <CommandEmpty>No service found.</CommandEmpty>
                          <CommandGroup>
                            {[...services]
                              .sort((a, b) => a.name.localeCompare(b.name))
                              .map((service) => (
                              <CommandItem
                                key={service.id}
                                value={service.name}
                                onSelect={async () => {
                                  await handleServiceSelect(service);
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    selectedService?.id === service.id ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                <div>
                                  <div className="font-medium">{service.name}</div>
                                  <div className="text-xs text-gray-500">{service.type}</div>
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
              
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setCurrentStep(selectedAppointment ? 1 : 2)} className="flex-1">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
                {selectedAppointment && (
                  <Button 
                    onClick={() => setCurrentStep(4)} 
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    Continue <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Step 4: Enter Amount */}
          {currentStep === 4 && (
            <div className="space-y-4">
              <div>
                <Label className="text-base font-semibold">Enter Amount</Label>
                <p className="text-sm text-gray-500 mb-3">How much is being collected?</p>
                <div className="relative">
                  <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-6 h-6 text-gray-400" />
                  <Input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0"
                    className="pl-12 text-2xl h-14 font-bold"
                    autoFocus
                  />
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setCurrentStep(3)} className="flex-1">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
                <Button 
                  onClick={handleAmountSubmit} 
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  disabled={amount === '' || parseFloat(amount) < 0}
                >
                  Continue <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 5: Split Payment Methods */}
          {currentStep === 5 && (
            <div className="space-y-4">
              <div>
                <Label className="text-base font-semibold">Payment Method(s)</Label>
                <p className="text-sm text-gray-500 mb-2">
                  Total: ₹{parseFloat(amount).toLocaleString()} 
                  {paymentSplits.length > 0 && (
                    <span className={getRemainingAmount() === 0 ? 'text-green-600' : 'text-orange-600'}>
                      {' '}• Remaining: ₹{getRemainingAmount().toLocaleString()}
                    </span>
                  )}
                </p>
                
                {/* Added payment splits */}
                {paymentSplits.length > 0 && (
                  <div className="space-y-2 mb-4">
                    {paymentSplits.map((split, index) => (
                      <div key={index} className={`flex items-center gap-2 p-3 rounded-xl border ${split.method?.id === COUPON_METHOD_ID ? 'bg-amber-50 border-amber-300' : 'bg-green-50 border-green-200'}`}>
                        <CreditCard className={`w-5 h-5 flex-shrink-0 ${split.method?.id === COUPON_METHOD_ID ? 'text-amber-700' : 'text-green-600'}`} />
                        <span className="font-medium text-gray-900 flex-1">{split.method.name}</span>
                        <div className="relative w-24">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-sm">₹</span>
                          <Input
                            type="number"
                            value={split.amount}
                            onChange={(e) => updateSplitAmount(index, e.target.value)}
                            readOnly={!!split.locked || split.method?.id === COUPON_METHOD_ID}
                            className={`pl-6 h-9 text-right font-semibold ${split.locked || split.method?.id === COUPON_METHOD_ID ? 'bg-amber-100/60 cursor-not-allowed' : ''}`}
                            data-testid={`payment-split-amount-${index}`}
                          />
                        </div>
                        <button
                          onClick={() => removeSplit(index)}
                          className="p-1 text-red-500 hover:bg-red-100 rounded"
                          data-testid={`payment-split-remove-${index}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* === Available Google-Review Coupons === */}
                {selectedPatient?.id && !paymentSplits.some(s => s.method?.id === COUPON_METHOD_ID) && activeCoupons.length > 0 && (
                  <div className="mb-4" data-testid="teller-coupon-picker">
                    <p className="text-xs font-semibold text-amber-800 mb-2 uppercase tracking-wide">
                      Available Coupons ({activeCoupons.length})
                    </p>
                    <div className="grid grid-cols-1 gap-2">
                      {activeCoupons.map((c) => {
                        const cVal = Number(c.discount_amount || 0);
                        const canApply = parseFloat(amount) >= cVal;
                        return (
                          <button
                            key={c.id}
                            onClick={() => applyCouponToPayment(c)}
                            disabled={!canApply}
                            className={`flex items-center gap-3 p-3 rounded-xl border text-left transition ${
                              canApply
                                ? 'border-amber-300 bg-amber-50 hover:bg-amber-100'
                                : 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                            }`}
                            data-testid={`teller-coupon-option-${c.code}`}
                          >
                            <Tag className="w-5 h-5 text-amber-700" />
                            <div className="flex-1">
                              <div className="font-semibold text-gray-900">{c.code}</div>
                              <div className="text-xs text-gray-600">
                                ₹{cVal} • Expires {c.expires_at ? new Date(c.expires_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                              </div>
                              {!canApply && (
                                <div className="text-xs text-red-600 mt-0.5">Bill must be ≥ ₹{cVal}</div>
                              )}
                            </div>
                            <span className="text-xs font-semibold text-amber-800">Apply</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Available payment methods */}
                <p className="text-xs text-gray-500 mb-2">
                  {paymentSplits.length === 0 ? 'Select payment method:' : 'Add another method (for split payment):'}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {paymentMethods.filter(m => !paymentSplits.find(s => s.method.id === m.id)).map(method => (
                    <button
                      key={method.id}
                      onClick={() => addPaymentSplit(method)}
                      className="p-3 rounded-xl border border-gray-200 hover:border-green-300 hover:bg-green-50/50 text-center transition"
                    >
                      <CreditCard className="w-5 h-5 mx-auto mb-1 text-gray-600" />
                      <div className="text-sm font-medium text-gray-900">{method.name}</div>
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setCurrentStep(4)} className="flex-1">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
                <Button 
                  onClick={handlePaymentMethodDone} 
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  disabled={paymentSplits.length === 0 || Math.abs(getRemainingAmount()) > 0.01}
                >
                  Continue <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 6: Select Counsellor/Staff */}
          {currentStep === 6 && (
            <div className="space-y-4">
              <div>
                <Label className="text-base font-semibold">Collected By</Label>
                <p className="text-sm text-gray-500 mb-3">Who is collecting the payment?</p>
                <div className="grid grid-cols-2 gap-2">
                  {counsellors.map(counsellor => (
                    <button
                      key={counsellor.id}
                      onClick={() => handleCounsellorSelect(counsellor)}
                      className={`p-4 rounded-xl border text-center transition ${
                        selectedCounsellor?.id === counsellor.id 
                          ? 'border-green-500 bg-green-50' 
                          : 'border-gray-200 hover:border-green-300 hover:bg-green-50/50'
                      }`}
                    >
                      <User className="w-6 h-6 mx-auto mb-2 text-gray-600" />
                      <div className="font-medium text-gray-900">{counsellor.name}</div>
                    </button>
                  ))}
                </div>
              </div>
              
              <Button variant="outline" onClick={() => setCurrentStep(5)} className="w-full">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
            </div>
          )}

          {/* Step 7: Confirmation & Remarks */}
          {currentStep === 7 && (
            <div className="space-y-4">
              <div>
                <Label className="text-base font-semibold">Confirm Payment</Label>
                <p className="text-sm text-gray-500 mb-3">Review details before creating receipt</p>
                
                <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Patient</span>
                    <span className="font-medium">{selectedPatient?.name || newPatientName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Phone</span>
                    <span className="font-medium">{phoneInput}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Service</span>
                    <span className="font-medium">{selectedService?.name}</span>
                  </div>
                  
                  {/* Payment Methods - show split if multiple */}
                  <div className="border-t pt-3 mt-3">
                    <span className="text-gray-500 text-sm">Payment Method{paymentSplits.length > 1 ? 's' : ''}</span>
                    <div className="mt-1 space-y-1">
                      {paymentSplits.map((split, index) => (
                        <div key={index} className="flex justify-between">
                          <span className="font-medium text-gray-700">{split.method.name}</span>
                          <span className="font-medium text-gray-900">₹{parseFloat(split.amount).toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-gray-500">Collected by</span>
                    <span className="font-medium">{selectedCounsellor?.name}</span>
                  </div>
                  <div className="flex justify-between border-t pt-3 mt-3">
                    <span className="text-gray-900 font-semibold">Total Amount</span>
                    <span className="text-xl font-bold text-green-600">₹{parseFloat(amount).toLocaleString()}</span>
                  </div>
                </div>
                
                <div className="mt-4">
                  <Label>Remarks (optional)</Label>
                  <Textarea
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Add any notes..."
                    className="mt-1"
                    rows={2}
                  />
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setCurrentStep(6)} className="flex-1">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
                <Button 
                  onClick={handleSavePayment} 
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Create Receipt
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Cancel Payment Modal */}
      <Dialog open={cancelModalOpen} onOpenChange={setCancelModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Cancel Payment</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel receipt {cancellingPayment?.receipt_number}?
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label>Reason for cancellation</Label>
            <Textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Enter reason..."
              className="mt-2"
              rows={3}
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelModalOpen(false)}>
              Keep Payment
            </Button>
            <Button 
              onClick={handleCancelPayment}
              className="bg-red-600 hover:bg-red-700"
            >
              Cancel Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ Redeem-from-Package Prompt ============ */}
      <Dialog open={!!redeemPrompt} onOpenChange={(o) => { if (!o) { setRedeemPrompt(null); setRedeemChoice(null); } }}>
        <DialogContent className="max-w-md" data-testid="pos-redeem-prompt">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PackageIcon className="w-5 h-5 text-purple-600" />
              Redeem from Package?
            </DialogTitle>
            <DialogDescription>
              <span className="font-semibold">{redeemPrompt?.service?.name}</span> is available in
              {' '}{(redeemPrompt?.packages || []).length === 1 ? 'this package' : `${(redeemPrompt?.packages || []).length} packages`}
              {' '}for this patient. Redeem 1 credit instead of charging?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2 max-h-64 overflow-y-auto">
            {(redeemPrompt?.packages || []).map(pkg => {
              const it = (pkg.items || []).find(i => i.service_id === redeemPrompt?.service?.id);
              const remaining = Number(it?.quantity_total || 0) - Number(it?.quantity_consumed || 0);
              const chosen = redeemChoice === pkg.id;
              return (
                <button
                  key={pkg.id}
                  onClick={() => setRedeemChoice(pkg.id)}
                  data-testid={`pos-redeem-pick-${pkg.ref_number}`}
                  className={`w-full text-left p-3 rounded-lg border transition ${
                    chosen ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-purple-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-mono text-xs text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded">{pkg.ref_number}</span>
                      <p className="text-sm mt-1">
                        <span className="font-semibold">{remaining}</span> credits left of {it?.quantity_total}
                      </p>
                    </div>
                    <div className="text-right text-[11px] text-gray-500">
                      Bal ₹{Number(pkg.balance || 0).toLocaleString()}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={skipRedeem}
              className="flex-1"
              data-testid="pos-redeem-skip"
            >
              Skip — Charge Normally
            </Button>
            <Button
              onClick={confirmRedeem}
              disabled={!redeemChoice || redeemSubmitting}
              className="flex-1 bg-purple-600 hover:bg-purple-700"
              data-testid="pos-redeem-confirm"
            >
              <Check className="w-4 h-4 mr-2" />
              {redeemSubmitting ? 'Redeeming…' : 'Redeem 1 Credit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default TellerPage;
